import { useEffect, useState } from "react";

import { AmplitudeFlagKey, AmplitudeVariantValue } from "@simplyk/common";

import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { trpc } from "@/common/helpers/trpc";
import { useAmplitudeFeatureFlag } from "@/common/hooks/amplitude/useAmplitudeFeatureFlag";
import { RouteType } from "@/common/hooks/amplitude/useLogRouteChangePageVisits";
import { SupportChannelResolution } from "@/components/ChatWidgetVisibilityManager/helpers/usePublishSupportChannel";
import { getChatWidgetRouteType } from "@/components/ChatWidgetVisibilityManager/utils";

type OrganizationSupportChannelReason =
  | "not_organization_route"
  | "amplitude_unavailable"
  | "variant_control"
  | "kill_switch"
  | "variant_treatment";

export type OrganizationSupportChannelResolution = SupportChannelResolution<OrganizationSupportChannelReason>;

export const useOrganizationSupportChannel = (pathWithoutLocale: string): OrganizationSupportChannelResolution => {
  const { currentUser, organization, userIsLoading } = useCurrentUserContext();
  const routeType = getChatWidgetRouteType(pathWithoutLocale);
  const isOrganizationPath = routeType === RouteType.OrganizationRoute;
  const userId = currentUser?.id;
  const organizationId = organization?.id;
  const isEligible = isOrganizationPath && Boolean(userId && organizationId);
  const evaluationKey = isEligible ? `${userId}:${organizationId}` : null;

  const {
    isActive,
    loading: isFlagLoading,
    value: flagValue,
  } = useAmplitudeFeatureFlag({
    flagKey: AmplitudeFlagKey.OrganizationAssistantWidget,
    userId,
    organizationId,
    userProperties: organizationId ? { organizationId } : undefined,
    fallbackOnFail: false,
    skip: !isEligible,
  });

  const {
    data: isOrganizationAssistantEnabled,
    isLoading: isKillSwitchLoading,
    isError: isKillSwitchError,
  } = trpc.getIsOrganizationAssistantEnabled.useQuery(
    { organizationId: organizationId ?? "" },
    { enabled: isEligible && isActive }
  );

  const [startedEvaluationKey, setStartedEvaluationKey] = useState<string | null>(null);

  useEffect(() => {
    setStartedEvaluationKey(evaluationKey);
  }, [evaluationKey]);

  if (isOrganizationPath && userIsLoading) {
    return { channel: "pending", routeType };
  }

  if (!isEligible) {
    return { channel: "forethought", reason: "not_organization_route", routeType };
  }

  if (isFlagLoading || startedEvaluationKey !== evaluationKey) {
    return { channel: "pending", routeType };
  }

  if (!isActive) {
    const isFlagUnresolved = flagValue === null || flagValue === AmplitudeVariantValue.OFF;
    return {
      channel: "forethought",
      reason: isFlagUnresolved ? "amplitude_unavailable" : "variant_control",
      routeType,
    };
  }

  if (!isKillSwitchError && (isKillSwitchLoading || isOrganizationAssistantEnabled === undefined)) {
    return { channel: "pending", routeType };
  }

  if (!isOrganizationAssistantEnabled) {
    return { channel: "forethought", reason: "kill_switch", routeType };
  }

  return { channel: "assistant", reason: "variant_treatment", routeType };
};
